import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  useDisclosure,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Badge,
  SimpleGrid,
  useColorModeValue,
  Image,
  Input,
} from "@chakra-ui/react";
import {
  MdRestaurant,
  MdMenuBook,
  MdCampaign,
  MdShoppingCart,
  MdInsights,
  MdHelpCenter,
  MdArrowForward,
  MdAssistant,
  MdClose,
} from "react-icons/md";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { authService } from "../../services/authService";
import { pricingService } from "../../services/pricingService";
import PremiumUpgradeModal from "../modals/PremiumUpgradeModal";
import { getBusinessSlug } from "../../utils/slugify";

// Waitron asistan için menü yapısı
const waitronSections = [
  {
    id: "business",
    title: "Profil Yönetimi",
    icon: MdRestaurant,
    actions: [
        {
            id: "business-info",
            label: "Restoran bilgilerimi düzenle",
            type: "navigate",
            path: "/profil-ayarlari",
          },
      {
        id: "working-hours",
        label: "Çalışma saatlerimi düzenle",
        type: "navigate",
        path: "/profil-ayarlari",
      },
      {
        id: "features",
        label: "Restoran özelliklerini düzenle",
        type: "navigate",
        path: "/profil-ayarlari",
      },
    ],
  },
  {
    id: "menu-pricing",
    title: "Menü Yönetimi",
    icon: MdMenuBook,
    actions: [
      {
        id: "menu-edit",
        label: "Menümü yönet",
        type: "navigate",
        path: "/menu",
      },
      {
        id: "menu-performance",
        label: "Menü performansını görüntüle",
        type: "navigate",
        path: "/analiz?section=menu-performance",
      },
    ],
  },
  {
    id: "campaigns-coins",
    title: "Kampanyalar",
    icon: MdCampaign,
    actions: [
      {
        id: "campaigns",
        label: "Kampanyalarımı yönet",
        type: "navigate",
        path: "/kampanyalar",
      },
      {
        id: "campaign-analysis",
        label: "Kampanya performansını görüntüle",
        type: "navigate",
        path: "/analiz?section=campaigns",
      },
      {
        id: "menomcoins-activate",
        label: "MenomCoins’i aktifleştir",
        type: "navigate",
        path: "/panel",
      },
    ],
  },
  {
    id: "orders-service",
    title: "Siparişler",
    icon: MdShoppingCart,
    actions: [
      {
        id: "today-orders",
        label: "Bugünkü siparişleri görüntüle",
        type: "navigate",
        path: "/siparisler",
      },
      {
        id: "orders",
        label: "Tüm siparişlerim",
        type: "navigate",
        path: "/siparisler",
      },
      {
        id: "orders-analysis",
        label: "Sipariş performansını görüntüle",
        type: "navigate",
        path: "/analiz?section=orders",
      },
    ],
  },
  {
    id: "analytics",
    title: "Analizler",
    icon: MdInsights,
    actions: [
      {
        id: "analysis-dashboard",
        label: "Analiz ve raporlama",
        type: "navigate",
        path: "/analiz",
      },
    ],
  },
  {
    id: "other",
    title: "Diğer",
    icon: MdAssistant,
    actions: [
      {
        id: "other-tables",
        label: "Nasıl masa eklerim?",
        type: "navigate",
        path: "/isletmem",
      },
      {
        id: "other-employees",
        label: "Nasıl çalışan eklerim?",
        type: "navigate",
        path: "/isletmem",
      },
      {
        id: "other-employee-performance",
        label: "Çalışan performansını görmek istiyorum",
        type: "navigate",
        path: "/analiz?section=employee-tasks",
      },
      {
        id: "other-tables-performance",
        label: "Masa performansını görmek istiyorum",
        type: "navigate",
        path: "/analiz?section=table-usage",
      },
    ],
  },
  {
    id: "help",
    title: "Destek & Yardım",
    icon: MdHelpCenter,
    actions: [
      {
        id: "support-website",
        label: "MenomSoft Destek",
        type: "navigate",
        path: "https://www.menomsoft.com",
      },
      {
        id: "support-email",
        label: "Destek Talebi Oluştur",
        type: "navigate",
        path: "mailto:info@menomsoft.com?subject=MenomSoft%20Business%20Destek%20Talep",
      },
    ],
  },
];

export default function WaitronAssistant() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [plan, setPlan] = useState("STANDARD");
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [customQuery, setCustomQuery] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { businessName } = useParams();

  const bgButton = useColorModeValue("white", "navy.700");
  const shadowButton = useColorModeValue(
    "0 10px 30px rgba(0, 0, 0, 0.14)",
    "0 10px 30px rgba(15, 23, 42, 0.65)"
  );

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        // Önce localStorage'dan hızlıca göster
        const business = authService.getCurrentBusiness();
        if (business && business.plan) {
          setPlan(business.plan);
        }

        // Ardından güncel plan bilgisini pricingService'ten al
        const pricingInfo = await pricingService.getPricingInfo();
        if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
          setPlan(pricingInfo.data.business.plan);
        }
      } catch (error) {
        console.error("Waitron plan bilgisi alınamadı:", error);
      } finally {
        setIsLoadingPlan(false);
      }
    };

    fetchPlan();
  }, []);

  const isPremium = plan === "PREMIUM";

  const handleOpenAssistant = () => {
    if (isLoadingPlan) return;

    // Sadece PREMIUM kullanıcılar için aktif
    if (!isPremium) {
      setIsUpgradeModalOpen(true);
      return;
    }

    onOpen();
  };

  const handleNavigateAction = (path) => {
    // Mail yönlendirmesi
    if (path.startsWith("mailto:")) {
      window.location.href = path;
      onClose();
      return;
    }

    // Harici link ise yeni sekmede aç
    if (path.startsWith("http://") || path.startsWith("https://")) {
      window.open(path, "_blank", "noopener,noreferrer");
      onClose();
      return;
    }

    const slug = businessName || getBusinessSlug();
    if (!slug) {
      // Fallback: mevcut admin path'i baz al
      const baseAdminPathMatch = location.pathname.match(/^\/admin\/[^/]+/);
      const baseAdminPath = baseAdminPathMatch ? baseAdminPathMatch[0] : "/admin";
      navigate(`${baseAdminPath}${path}`);
    } else {
      navigate(`/admin/${slug}${path}`);
    }
    onClose();
  };

  const activeSection =
    waitronSections.find((section) => section.id === activeSectionId) ||
    null;

  // Ana başlık seçildiğinde küçük açıklama balonu ekleyelim
  const handleSectionSelect = (section) => {
    // Aynı başlığa tekrar tıklandıysa yeni mesaj ekleme
    if (activeSectionId === section.id) {
      return;
    }
    setActiveSectionId(section.id);

    const now = Date.now();
    let text = "";
    switch (section.id) {
      case "business":
        text =
          "İşletmenle ilgili temel ayarlar için buradayım. Aşağıdan yapmak istediğin işlemi seçebilirsin.";
        break;
      case "menu-pricing":
        text =
          "Menü ve fiyatlarla ilgili işlemlerde yardımcı olabilirim. Devam etmek için bir işlem seç.";
        break;
      case "campaigns-coins":
        text =
          "Kampanyalar ve sadakat özellikleri ile ilgili ayarları buradan yönetebilirsin.";
        break;
      case "orders-service":
        text =
          "Sipariş akışı ile ilgili işlemler için aşağıdaki seçenekleri kullan.";
        break;
      case "analytics":
        text =
          "Analiz ve raporlamaları görmek için hangi alana odaklanmak istediğini seç.";
        break;
      case "help":
        text =
          "Destek ve yardım için aşağıdaki seçeneklerden birini kullanabilirsin.";
        break;
      default:
        text =
          "Devam etmek için aşağıdaki işlemlerden birini seçebilirsin.";
        break;
    }

    setMessages((prev) => [
      ...prev,
      {
        id: `${now}-assistant-section-${section.id}`,
        role: "assistant",
        text,
      },
    ]);
  };

  // Aksiyonlara göre asistanın vereceği basit yanıtlar
  const getAssistantResponse = (action) => {
    switch (action.id) {
      case "business-info":
        return "Restoran adın, açıklaman ve görsellerin Profil Ayarları > Genel Bilgiler sekmesinden güncellenir.";
      case "working-hours":
        return "Çalışma saatlerini Profil Ayarları > Çalışma Saatleri sekmesinden yönetebilirsin. İstersen şimdi seni oraya götürebilirim.";
      case "features":
        return "Restoran özelliklerini (WiFi, otopark vb.) Profil Ayarları > Özellikler sekmesinden açıp kapatabilirsin.";
      case "menu-edit":
        return "Menüde kategori ve ürün ekleme/düzenleme işlemlerini Menü sayfasından yapabilirsin.";
      case "menu-performance":
        return "Menü Performansı analizinde en çok satan ürünleri ve kategori performansını takip edebilirsin.";
      case "campaigns":
        return "Kampanyalarını Kampanyalar sayfasından oluşturup yönetebilirsin.";
      case "campaign-analysis":
        return "Kampanya görselleştirmeleri Analiz sayfasındaki kampanya sekmesinde yer alıyor.";
      case "menomcoins-activate":
        return "MenomCoins ile ilgili ayarları ve durumunu ana kontrol panelinden takip edebilirsin. Şimdi seni oraya götürebilirim.";
      case "other-tables":
        return "Yeni masa eklemek, masa silmek veya QR kodlarını yönetmek için İşletmem sayfasındaki Masalar bölümünü kullanabilirsin.";
      case "other-employees":
        return "Yeni çalışan eklemek ve çalışanlarını yönetmek için İşletmem sayfasındaki çalışanlar bileşenini kullanabilirsin (Premium ile görünür).";
      case "other-employee-performance":
        return "Çalışan performansı ve görev analizi için Analiz sayfasındaki Çalışan & Görev Analizi bölümünü kullanabilirsin.";
      case "today-orders":
        return "Bugünkü tüm siparişlerini Siparişler sayfasından takip edebilirsin.";
      case "orders":
        return "Sipariş geçmişin ve detayları Siparişler ekranında listelenir.";
      case "analysis-dashboard":
        return "Gelir, menü ve çeşitli analizleri Analiz ve Raporlama sayfasında görebilirsin.";
      case "support-website":
        return "Daha detaylı dokümanlar ve sıkça sorulan sorular için MenomSoft Destek sayfasına gidebilirsin.";
      case "support-email":
        return "Bize bir destek talebi göndermek için e-posta oluşturacağım. Açılan mailde kısaca sorununuzu yazmanız yeterli.";
      default:
        return "Bu işlem için ilgili sayfaya giderek detaylı ayar yapabilirsin. İstersen seni şimdi oraya götürebilirim.";
    }
  };

  // Serbest metin için en yakın aksiyonu bul
  const findBestActionByQuery = (query) => {
    const q = query.toLowerCase();

    // Manuel keyword -> action eşleştirmeleri
    if (q.includes("masa")) return { sectionId: "other", actionId: "other-tables" };
    if (q.includes("çalışan") || q.includes("garson"))
      return { sectionId: "other", actionId: "other-employees" };
    if (q.includes("performans") && (q.includes("çalışan") || q.includes("garson")))
      return { sectionId: "other", actionId: "other-employee-performance" };
    if (q.includes("menomcoin") || q.includes("menomcoins"))
      return { sectionId: "campaigns-coins", actionId: "menomcoins-activate" };
    if (q.includes("sipariş") && (q.includes("performans") || q.includes("analiz")))
      return { sectionId: "orders-service", actionId: "orders-analysis" };

    // Label içinde geçen ilk eşleşmeye göre basit fallback
    for (const section of waitronSections) {
      for (const action of section.actions) {
        if (action.label.toLowerCase().includes(q)) {
          return { sectionId: section.id, actionId: action.id };
        }
      }
    }

    return null;
  };

  const handleFreeTextSubmit = () => {
    const text = customQuery.trim();
    if (!text) return;

    const now = Date.now();

    // Kullanıcı mesajını ekle
    const userMessage = {
      id: `${now}-user-free`,
      role: "user",
      text,
    };

    const match = findBestActionByQuery(text);

    if (!match) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          id: `${now}-assistant-free-none`,
          role: "assistant",
          text:
            "Şu anda bu soruya özel bir akışım yok. Ancak üstteki başlıklardan birini seçerek ilgili işlemlere hızlıca gidebilirsin.",
        },
      ]);
      setCustomQuery("");
      return;
    }

    const section = waitronSections.find((s) => s.id === match.sectionId);
    const action = section?.actions.find((a) => a.id === match.actionId);

    if (!section || !action) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          id: `${now}-assistant-free-error`,
          role: "assistant",
          text:
            "Bir şeyler ters gitti, ama üstteki başlıklardan birini seçerek devam edebilirsin.",
        },
      ]);
      setCustomQuery("");
      return;
    }

    setActiveSectionId(section.id);

    const assistantText =
      `Soruna en yakın olarak "${action.label}" işlemini buldum. ` +
      getAssistantResponse(action);

    setMessages((prev) => [
      ...prev,
      userMessage,
      {
        id: `${now}-assistant-free-match`,
        role: "assistant",
        text: assistantText,
        path: action.path,
      },
    ]);

    setCustomQuery("");
  };

  const handleActionClick = (action) => {
    const now = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: `${now}-user`,
        role: "user",
        text: action.label,
      },
      {
        id: `${now}-assistant`,
        role: "assistant",
        text: getAssistantResponse(action),
        path: action.path,
      },
    ]);
  };

  // Panel açıldığında karşılama mesajı
  useEffect(() => {
    if (isOpen) {
      const now = Date.now();
      setMessages([
        {
          id: `${now}-assistant-intro`,
          role: "assistant",
          text:
            "Merhaba, ben Waitron. İşletme panelinde sana hızlıca yardımcı olabilirim. "
        },
      ]);
      setActiveSectionId(null);
    }
  }, [isOpen]);

  if (isLoadingPlan) {
    // Plan yüklenene kadar butonu göstermeyelim (göz kırpması olmasın)
    return null;
  }

  return (
    <>
      {/* Sağ alttaki Waitron butonu */}
      <Box
        position="fixed"
        bottom={{ base: "20px", md: "30px" }}
        right={{ base: "20px", md: "30px" }}
        zIndex={1000}
      >
        <Tooltip
          label={
            isPremium
              ? "Waitron ile panelde hızlı yardım al"
              : "Waitron, PREMIUM işletmeler için kullanılabilir"
          }
          placement="left"
        >
          <IconButton
            aria-label="Waitron Asistan"
            icon={
              <Image
                src="/Waitron (1).png"
                alt="Waitron"
                boxSize="40px"
                objectFit="contain"
              />
            }
            colorScheme={isPremium ? "red" : "gray"}
            variant="solid"
            size="lg"
            borderRadius="full"
            boxSize="64px"
            borderWidth="2px"
            borderColor={isPremium ? "red.400" : "gray.300"}
            boxShadow={shadowButton}
            bg={bgButton}
            color={isPremium ? "red.500" : "gray.500"}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow:
                "0 15px 35px rgba(0, 0, 0, 0.18)",
            }}
            onClick={handleOpenAssistant}
          />
        </Tooltip>
      </Box>

      {/* Waitron sohbet paneli (chat widget tarzı) - dışarı tıklayınca kapanır */}
      {isOpen && (
        <Box
          position="fixed"
          inset={0}
          zIndex={1001}
          onClick={onClose}
        >
          <Box
            position="absolute"
            bottom={{ base: "100px", md: "110px" }}
            right={{ base: "20px", md: "30px" }}
            w={{ base: "280px", md: "360px" }}
            maxH="70vh"
            bg="white"
            borderRadius="xl"
            boxShadow="0 18px 45px rgba(15, 23, 42, 0.35)"
            borderWidth="1px"
            borderColor="gray.200"
            display="flex"
            flexDirection="column"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box
              px={4}
              py={3}
              borderBottomWidth="1px"
              borderColor="gray.100"
              bg="gray.50"
            >
              <HStack spacing={3}>
                <Image
                  src="/Waitron (1).png"
                  alt="Waitron"
                  boxSize="28px"
                  objectFit="contain"
                />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" fontSize="sm">
                    Waitron
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    İşletme paneliniz için hızlı asistan
                  </Text>
                </VStack>
                <Badge
                  colorScheme="yellow"
                  variant="subtle"
                  ml="auto"
                  fontSize="0.6rem"
                >
                  PREMIUM
                </Badge>
                <IconButton
                  aria-label="Kapat"
                  icon={<Icon as={MdClose} boxSize={3} />}
                  size="xs"
                  variant="ghost"
                  onClick={onClose}
                />
              </HStack>
            </Box>

            {/* Body */}
            <Box px={3} py={3} flex="1" overflowY="auto">
              <VStack align="stretch" spacing={4}>
                {/* Sohbet balonları (ilk açılışta karşılama + sonrasında yanıtlar) */}
                {messages.length > 0 && (
                  <Box>
                    <VStack align="stretch" spacing={2}>
                      {messages.map((msg) => {
                        const isUser = msg.role === "user";
                        return (
                          <HStack
                            key={msg.id}
                            spacing={2}
                            align="flex-end"
                            justify={isUser ? "flex-end" : "flex-start"}
                          >
                            {/* Waitron balonu - solda ikon + balon */}
                            {!isUser && (
                              <>
                                <Image
                                  src="/Waitron (1).png"
                                  alt="Waitron"
                                  boxSize="20px"
                                  objectFit="contain"
                                />
                                <Box
                                  bg="gray.200"
                                  color="#365fab"
                                  px={3}
                                  py={2}
                                  borderRadius="20px"
                                  borderBottomLeftRadius="1px"
                                  fontSize="xs"
                                  maxW="75%"
                                  boxShadow="0 4px 10px rgba(0,0,0,0.10)"
                                >
                                  <Text>{msg.text}</Text>
                                  {msg.path && (
                                    <Button
                                      size="xs"
                                      mt={2}
                                      variant="outline"
                                      colorScheme="red"
                                      onClick={() =>
                                        handleNavigateAction(msg.path)
                                      }
                                    >
                                      İlgili sayfaya git
                                    </Button>
                                  )}
                                </Box>
                              </>
                            )}

                            {/* Kullanıcı balonu - sağda balon + küçük avatar */}
                            {isUser && (
                              <>
                                <Box
                                  bg="red.600"
                                  color="white"
                                  px={3}
                                  py={2}
                                  borderRadius="20px"
                                  borderBottomRightRadius="4px"
                                  fontSize="xs"
                                  maxW="75%"
                                  boxShadow="0 4px 10px rgba(0,0,0,0.18)"
                                >
                                  <Text>{msg.text}</Text>
                                </Box>
                                <Box
                                  boxSize="20px"
                                  borderRadius="full"
                                  bg="red.700"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <Text fontSize="0.6rem" color="white">
                                    Siz
                                  </Text>
                                </Box>
                              </>
                            )}
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Box>
                )}

                {/* Konu seçimi */}
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Ne yapmak istiyorsunuz?
                  </Text>
                  <SimpleGrid columns={2} spacing={2}>
                    {waitronSections.map((section) => (
                      <Button
                        key={section.id}
                        variant={
                          section.id === activeSectionId
                            ? "solid"
                            : "outline"
                        }
                        colorScheme="red"
                        justifyContent="center"
                        onClick={() => handleSectionSelect(section)}
                        size="xs"
                        whiteSpace="normal"
                        height="auto"
                        fontSize="xs"
                        py={2}
                        w="100%"
                      >
                        <HStack spacing={1} justify="center" w="100%">
                          <Icon as={section.icon} boxSize={3} />
                          <Text textAlign="center">{section.title}</Text>
                        </HStack>
                      </Button>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Seçilen konuya göre alt seçenekler */}
                {activeSection && (
                  <Box>
                    <HStack mb={2}>
                      <Text fontSize="xs" fontWeight="600">
                        {activeSection.title}
                      </Text>
                    </HStack>
                    <VStack align="stretch" spacing={1}>
                      {activeSection.actions.map((action) => (
                        <Button
                          key={action.id}
                          variant="ghost"
                          justifyContent="center"
                          onClick={() => handleActionClick(action)}
                          size="sm"
                          fontSize="sm"
                          py={2}
                          w="100%"
                        >
                          <HStack spacing={1} justify="center" w="100%">
                            <Text fontSize="sm" fontWeight="500" textAlign="center">
                              {action.label}
                            </Text>
                            <Icon as={MdArrowForward} boxSize={3} />
                          </HStack>
                        </Button>
                      ))}
                    </VStack>
                  </Box>
                )}

              </VStack>
            </Box>
            {/* Serbest metinle soru sorma - altta sabit */}
            <Box
              borderTopWidth="1px"
              borderColor="gray.100"
              px={3}
              py={2}
              bg="white"
            >
              <Text fontSize="xs" color="gray.500" mb={1}>
                Aklındaki başka bir soruyu da yazabilirsin:
              </Text>
              <HStack spacing={2}>
                <Input
                  size="sm"
                  placeholder="Örn: Masa nasıl eklerim?"
                  value={customQuery}
                  onChange={(e) => setCustomQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFreeTextSubmit();
                    }
                  }}
                />
                <Button
                  size="sm"
                  colorScheme="red"
                  onClick={handleFreeTextSubmit}
                >
                  Gönder
                </Button>
              </HStack>
            </Box>
          </Box>
        </Box>
      )}

      {/* Premium upgrade modal - PREMIUM olmayanlar için */}
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onConfirm={async () => {
          // Plan bilgisini güncelle
          try {
            const pricingInfo = await pricingService.getPricingInfo();
            if (
              pricingInfo?.success &&
              pricingInfo?.data?.business?.plan
            ) {
              const newPlan = pricingInfo.data.business.plan;
              setPlan(newPlan);

              // localStorage içindeki business objesini de güncelle
              const business = authService.getCurrentBusiness();
              if (business) {
                business.plan = newPlan;
                localStorage.setItem(
                  "business",
                  JSON.stringify(business)
                );
              }
            }
          } catch (error) {
            console.error(
              "Waitron için premium plana geçiş sonrası plan güncellenemedi:",
              error
            );
          }
        }}
      />
    </>
  );
}


